import React, { useCallback, useState } from 'react';
import type { FC } from 'react';
import { FormattedMessage, useIntl } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import ButtonGroup from '@atlaskit/button/button-group';
import Button from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import Form, {
	Field,
	FormFooter,
	CharacterCounterField,
	ErrorMessage,
	MessageWrapper,
} from '@atlaskit/form';
import Heading from '@atlaskit/heading';
import { CloseButton, ModalBody, ModalFooter, useModal } from '@atlaskit/modal-dialog';
import { Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import Textfield from '@atlaskit/textfield';
import { token } from '@atlaskit/tokens';
import TextArea from '@atlaskit/textarea';

import { ADFRenderer } from '@confluence/adf-renderer/entry-points/ADFRenderer.ts';
import { useChoreographerAssets } from '@confluence/choreographer-services/entry-points/useChoreographerAssets.ts';
import { withFlags, WithFlagsProps } from '@confluence/flags/entry-points/withFlags.ts';

import { messages } from '../messages.ts';
import type { Snippet } from '../snippetTypes.ts';
import { useCreateSnippet } from '../hooks/useCreateSnippet.ts';

type CreateSnippetModalHeaderProps = {
	onClose: () => void;
};

type CreateSnippetModalDialogProps = {
	onCloseComplete: () => void;
} & CreateSnippetModalHeaderProps &
	WithFlagsProps;

export type CreateSnippetModalProps = {
	adf: any;
	onCreate: (snippet: Snippet) => void;
} & CreateSnippetModalDialogProps;

export enum CreateSnippetModalEvents {
	CANCELLED = 'createSnippetModalCancelled',
	CLOSED = 'createSnippetModalClosed',
	CREATED = 'createSnippetModalCreated',
}

interface CreateSnippetModalForm {
	snippetName: string;
	snippetDescription: string;
}

export const MAX_SNIPPET_NAME_LENGTH = 60;
export const MAX_SNIPPET_DESCRIPTION_LENGTH = 120;

const styles = cssMap({
	formContainer: {
		display: 'flex',
		flexDirection: 'column',
		height: '100%',
	},
	headerContainer: {
		padding: token('space.300'),
	},
	bodyContainer: {
		height: '100%',
	},
	contentContainer: {
		padding: token('space.300'),
		overflow: 'auto',
		maxHeight: '300px',
		border: `${token('border.width')} solid ${token('color.border.input')}`,
		borderRadius: token('radius.large'),
		scrollbarColor: `${token('color.border.input')} transparent`,
	},
});

const CreateSnippetModalHeader = ({ onClose }: CreateSnippetModalHeaderProps) => {
	const { titleId } = useModal();

	return (
		<Inline
			alignInline="center"
			spread="space-between"
			alignBlock="start"
			xcss={styles.headerContainer}
		>
			<Stack space="space.050">
				<Heading size="medium" id={titleId}>
					<FormattedMessage {...messages.createTemplateModalTitle} />
				</Heading>
				<Text size="medium" color="color.text.subtle">
					<FormattedMessage {...messages.createTemplateModalDescription} />
				</Text>
			</Stack>
			<CloseButton onClick={onClose} />
		</Inline>
	);
};

const CreateSnippetModalWithFlags: FC<CreateSnippetModalProps> = ({
	adf,
	onClose,
	onCreate,
	flags,
}) => {
	const [adfError, setADFError] = useState<any>(null);

	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { Modal } = useChoreographerAssets();
	const intl = useIntl();

	const onSuccess = useCallback(
		(snippet: Snippet) => {
			onCreate(snippet);
			flags.showSuccessFlag({
				title: intl.formatMessage(messages.createTemplateModalSuccessFlagTitle, {
					snippetName: snippet.name,
				}),
				description: intl.formatMessage(messages.createTemplateModalSuccessFlagDescription),
				isAutoDismiss: true,
			});
		},
		[onCreate, flags, intl],
	);

	const onFailure = useCallback(() => {
		flags.showErrorFlag({
			title: intl.formatMessage(messages.createTemplateModalErrorFlagTitle),
			description: intl.formatMessage(messages.createTemplateModalErrorFlagDescription),
			isAutoDismiss: true,
		});
	}, [flags, intl]);

	const { create } = useCreateSnippet(onSuccess, onFailure);

	const fireCreateSnippetModalAnalytics = useCallback(
		(event: CreateSnippetModalEvents) => {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					source: 'confluenceCreateSnippetModal',
					action: 'closed',
					actionSubject: 'button',
					actionSubjectId: 'createSnippet',
					type: 'ui',
					attributes: {
						event,
					},
				},
			}).fire();
		},
		[createAnalyticsEvent],
	);

	const exitModal = useCallback(
		(event: CreateSnippetModalEvents) => {
			fireCreateSnippetModalAnalytics(event);
			onClose();
		},
		[fireCreateSnippetModalAnalytics, onClose],
	);

	const submit = useCallback(
		async (values: CreateSnippetModalForm) => {
			await create(values.snippetName, adf, values.snippetDescription);
			fireCreateSnippetModalAnalytics(CreateSnippetModalEvents.CREATED);
			onClose();
		},
		[fireCreateSnippetModalAnalytics, onClose, adf, create],
	);

	const handleADFError = useCallback(
		(error: any) => {
			setADFError(error);
		},
		[setADFError],
	);

	return (
		<Modal height="750px">
			<CreateSnippetModalHeader onClose={() => exitModal(CreateSnippetModalEvents.CLOSED)} />
			<Form<CreateSnippetModalForm> noValidate onSubmit={submit}>
				{({ submitting, formProps }) => (
					<form {...formProps} css={styles.formContainer}>
						<ModalBody>
							<Stack space="space.300" xcss={styles.bodyContainer}>
								<Field
									name="snippetName"
									label={intl.formatMessage(messages.createTemplateModalNameLabel)}
									defaultValue=""
									helperMessage={intl.formatMessage(messages.createTemplateModalNameHelper)}
									component={({ fieldProps }) => (
										<Textfield
											{...fieldProps}
											maxLength={MAX_SNIPPET_NAME_LENGTH}
											placeholder={intl.formatMessage(messages.createTemplateModalNamePlaceholder)}
										/>
									)}
									validate={(value) =>
										value ? undefined : intl.formatMessage(messages.createTemplateModalNameError)
									}
									isRequired
								/>
								<CharacterCounterField<string, HTMLTextAreaElement>
									name="snippetDescription"
									label={intl.formatMessage(messages.createTemplateModalDescriptionLabel)}
									maxCharacters={MAX_SNIPPET_DESCRIPTION_LENGTH}
								>
									{({ fieldProps }) => (
										<TextArea
											{...fieldProps}
											placeholder={intl.formatMessage(
												messages.createTemplateModalDescriptionPlaceholder,
											)}
										/>
									)}
								</CharacterCounterField>
								<Stack grow="fill">
									{/* This <Stack> is here to make sure it has full height and the ADF renderer can be displayed properly */}
									<Stack grow="fill" xcss={styles.contentContainer}>
										<ADFRenderer
											document={adf}
											appearance="comment"
											allowPlaceholderText
											onError={handleADFError}
										/>
									</Stack>
									<Field
										name="snippetBody"
										defaultValue={adfError} // Must be here to make sure validation works properly.
										validate={() =>
											adfError
												? intl.formatMessage(messages.createTemplateModalAdfError)
												: undefined
										}
									>
										{({ error }) => (
											// This <Field> is here to show the error message when the ADF is invalid
											// and for <Form> validation to work comprehensively for name, description,
											// and ADF and prevent `Create` from being triggered for all cases.
											<MessageWrapper>
												{error && <ErrorMessage>{error}</ErrorMessage>}
											</MessageWrapper>
										)}
									</Field>
								</Stack>
							</Stack>
						</ModalBody>

						<ModalFooter>
							<FormFooter>
								<ButtonGroup>
									<Button
										appearance="subtle"
										onClick={() => exitModal(CreateSnippetModalEvents.CANCELLED)}
									>
										<FormattedMessage {...messages.createTemplateModalCancelButton} />
									</Button>
									<Button type="submit" appearance="primary" isLoading={submitting}>
										<FormattedMessage {...messages.createTemplateModalCreateButton} />
									</Button>
								</ButtonGroup>
							</FormFooter>
						</ModalFooter>
					</form>
				)}
			</Form>
		</Modal>
	);
};

export const CreateSnippetModal = withFlags(CreateSnippetModalWithFlags);
